import React from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";

import { downloadInternalFairReportsV2, fetchInternalFairReportsV2 } from "@/api/events";
import Pagination from "@/components/modules/Dashboard/dataTabs/liveReport/pagination";
import NoDataPreview from "@/components/shared/noDataPreview";
import { showToasterMessage } from "@/store/actions/commonActions";
import { toasterMessage } from "@/utils/toaster-msg";
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    MenuItem,
    TextField,
    Typography,
    makeStyles,
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";
import {
    buildUniversityStaffMap,
    fetchInternalFairUniversitiesDirectory,
    formatSingleUniversityWithStaffName,
} from "./utils";

const useStyles = makeStyles((theme) => ({
    filtersCard: {
        background: "#fafafa",
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    filterField: {
        width: "100%",
        background: theme.palette.common.white,
    },
    actionsRow: {
        display: "flex",
        gap: theme.spacing(1),
        justifyContent: "flex-end",
        marginBottom: theme.spacing(2),
        [theme.breakpoints.down("sm")]: {
            justifyContent: "stretch",
            "& > button": {
                flex: 1,
            },
        },
    },
    studentCard: {
        marginBottom: theme.spacing(1.5),
        border: "1px solid #d9e3ea",
        borderRadius: theme.spacing(1),
        background: "linear-gradient(180deg, #ffffff 0%, #fbfcfd 100%)",
        padding: theme.spacing(1.75, 2),
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    },
    studentSummaryGrid: {
        width: "100%",
        alignItems: "flex-start",
    },
    summaryLabel: {
        fontSize: "12px",
        color: "#6b7a88",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        marginBottom: theme.spacing(0.3),
    },
    summaryValue: {
        fontWeight: 500,
        wordBreak: "break-word",
        color: "#183247",
    },
    secondarySection: {
        marginTop: theme.spacing(1.25),
        paddingTop: theme.spacing(1.25),
        borderTop: "1px solid #e8eef2",
        background: "#f8fbfc",
        borderRadius: theme.spacing(0.75),
        padding: theme.spacing(1.5, 1.5, 1.25),
    },
    detailLabel: {
        fontWeight: 600,
        marginBottom: theme.spacing(0.5),
        fontSize: "12px",
        color: "#557086",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
    },
    detailValue: {
        wordBreak: "break-word",
        whiteSpace: "pre-wrap",
        lineHeight: 1.5,
        color: "#243746",
    },
    notesValue: {
        fontSize: "13px",
        lineHeight: 1.55,
        color: "#5f7180",
        fontWeight: 400,
    },
    noteRow: {
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(1),
        flexWrap: "wrap",
        "& + &": {
            marginTop: theme.spacing(0.85),
        },
    },
    noteUniversity: {
        color: "#27485d",
        fontWeight: 600,
        lineHeight: 1.45,
    },
    noteStaff: {
        color: "#5f7180",
        fontStyle: "italic",
        fontWeight: 400,
        lineHeight: 1.45,
    },
    noteComments: {
        color: "#5f7180",
        fontSize: "13px",
        lineHeight: 1.55,
        fontWeight: 400,
        wordBreak: "break-word",
    },
    emptyNotesState: {
        color: "#6e7f8c",
        fontSize: "13px",
        lineHeight: 1.5,
        fontStyle: "italic",
    },
    warningNotesState: {
        color: "#c62828",
        fontSize: "13px",
        lineHeight: 1.5,
        fontWeight: 500,
    },
    notesPanel: {
        background: "#f8fbfc",
        borderRadius: theme.spacing(0.75),
        padding: theme.spacing(1.1, 1.25),
        border: "1px solid #e8eef2",
    },
    emphasisValue: {
        color: "#0f4c5c",
        fontWeight: 600,
    },
}));

const initialFilters = {
    attendance_filter: "all",
    university: "",
    student_name: "",
    unique_code: "",
};

const getDisplayValue = (value) => (value === "" || value === undefined || value === null ? "-" : value);
const hasNotes = (student) => Array.isArray(student.formattedNotes) && student.formattedNotes.length > 0;

export default function StudentReportTab({ fairId, onTotalStudentsChange, jsonUrl }) {
    const classes = useStyles();
    const dispatch = useDispatch();

    const [loading, setLoading] = React.useState(false);
    const [exporting, setExporting] = React.useState(false);
    const [filters, setFilters] = React.useState(initialFilters);
    const [page, setPage] = React.useState(1);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [data, setData] = React.useState([]);
    const [total, setTotal] = React.useState(0);
    const [universityStaffMap, setUniversityStaffMap] = React.useState({});

    const buildReportQuery = React.useCallback(
        (activePage = page, activeRowsPerPage = rowsPerPage) => {
            const query = {
                fair_id: fairId,
                page: activePage,
                per_page: activeRowsPerPage,
                attendance_filter: filters.attendance_filter,
            };

            if (filters.university.trim()) query.university = filters.university.trim();
            if (filters.student_name.trim()) query.student_name = filters.student_name.trim();
            if (filters.unique_code.trim()) query.unique_code = filters.unique_code.trim();

            return query;
        },
        [fairId, filters, page, rowsPerPage]
    );

    const buildExportQuery = React.useCallback(() => {
        const query = {
            fair_id: fairId,
        };

        if (filters.university.trim()) query.university = filters.university.trim();
        if (filters.student_name.trim()) query.student_name = filters.student_name.trim();
        if (filters.unique_code.trim()) query.unique_code = filters.unique_code.trim();

        return query;
    }, [fairId, filters]);

    const loadReports = React.useCallback(
        (activePage = page, activeRowsPerPage = rowsPerPage) => {
            if (!fairId) return Promise.resolve();

            setLoading(true);
            return fetchInternalFairReportsV2(buildReportQuery(activePage, activeRowsPerPage))
                .then((res) => {
                    setData(res.data || []);
                    setTotal(Number(res.headers.total_count || 0));
                    onTotalStudentsChange(Number(res.headers.total_students_count || 0));
                })
                .catch(() => {
                    setData([]);
                    setTotal(0);
                    onTotalStudentsChange(0);
                })
                .finally(() => setLoading(false));
        },
        [buildReportQuery, fairId, onTotalStudentsChange, page, rowsPerPage]
    );

    React.useEffect(() => {
        setPage(1);
        loadReports(1, rowsPerPage);
    }, [fairId]);

    React.useEffect(() => {
        fetchInternalFairUniversitiesDirectory({ jsonUrl })
            .then((directory) => {
                setUniversityStaffMap(buildUniversityStaffMap(directory));
            })
            .catch(() => {
                setUniversityStaffMap({});
            });
    }, [jsonUrl]);

    const handleFilterChange = (key) => (event) => {
        const { value } = event.target;
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const handleSearch = () => {
        setPage(1);
        loadReports(1, rowsPerPage);
    };

    const handleRefresh = () => {
        loadReports(page, rowsPerPage);
    };

    const handleExport = () => {
        if (!fairId || exporting) return;

        setExporting(true);
        downloadInternalFairReportsV2(buildExportQuery())
            .then((res) => {
                const downloadUrl = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement("a");
                const match = (res.headers["content-disposition"] || "").match(/filename=(.*?)\.(.*)/);

                let filename = `internal_fair_attended_students_${fairId}`;
                let ext = "xlsx";
                if (match) {
                    filename = match[1].trim();
                    ext = match[2];
                }

                link.download = `${filename}.${ext}`;
                link.href = downloadUrl;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(downloadUrl);
            })
            .catch(() => {
                dispatch(
                    showToasterMessage(
                        toasterMessage.reportDownloadFail.msg,
                        toasterMessage.reportDownloadFail.color
                    )
                );
            })
            .finally(() => setExporting(false));
    };

    const handleChangePage = (_, pageNumber) => {
        const nextPage = pageNumber + 1;
        setPage(nextPage);
        window.scrollTo({ top: 0 });
        loadReports(nextPage, rowsPerPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const nextRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(nextRowsPerPage);
        setPage(1);
        window.scrollTo({ top: 0 });
        loadReports(1, nextRowsPerPage);
    };

    const renderStudentRows = () => {
        if (loading) {
            return (
                <Box textAlign="center" py={4}>
                    <CircularProgress color="primary" />
                </Box>
            );
        }

        if (data.length === 0) {
            return <NoDataPreview title="No students found" />;
        }

        return data.map((student) => {
                return (
                    <Box key={student.registrantCode} className={classes.studentCard}>
                        <Grid container spacing={2} className={classes.studentSummaryGrid}>
                            <Grid item xs={12} sm={6} md={2}>
                                <Typography className={classes.summaryLabel}>Unique Id</Typography>
                                <Typography className={classes.summaryValue}>
                                    {getDisplayValue(student.registrantCode)}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <Typography className={classes.summaryLabel}>Student Name</Typography>
                                <Typography className={classes.summaryValue}>{getDisplayValue(student.name)}</Typography>
                            </Grid>
                            {hasNotes(student) || (student.missingAttendedUniversities || []).length > 0 ? (
                                <Grid item xs={12} md={8}>
                                    <Typography className={classes.summaryLabel}>Notes</Typography>
                                    <Box className={classes.notesPanel}>
                                        {hasNotes(student)
                                            ? student.formattedNotes.map((note, index) => {
                                                  const noteMeta = formatSingleUniversityWithStaffName(
                                                      note.universityName,
                                                      universityStaffMap
                                                  );

                                                  return (
                                                      <Box key={`${student.registrantCode}-note-${index}`} className={classes.noteRow}>
                                                          {note.universityName && note.universityName !== "-" ? (
                                                              <>
                                                                  <Typography variant="body2" className={classes.noteUniversity}>
                                                                      {noteMeta.universityName}
                                                                  </Typography>
                                                                  {noteMeta.staffText ? (
                                                                      <Typography variant="body2" className={classes.noteStaff}>
                                                                          ({noteMeta.staffText})
                                                                      </Typography>
                                                                  ) : null}
                                                              </>
                                                          ) : null}
                                                          <Typography variant="body2" className={classes.noteComments}>
                                                              {note.universityName && note.universityName !== "-" ? ":" : ""}
                                                              {note.universityName && note.universityName !== "-" ? " " : ""}
                                                              {(note.comments || []).join(", ")}
                                                          </Typography>
                                                      </Box>
                                                  );
                                              })
                                            : null}
                                        {(student.missingAttendedUniversities || []).map((item, index) => {
                                            const attendedMeta = formatSingleUniversityWithStaffName(
                                                item.universityName,
                                                universityStaffMap
                                            );

                                            return (
                                                <Box
                                                    key={`${student.registrantCode}-missing-attended-${index}`}
                                                    className={classes.noteRow}
                                                >
                                                    <Typography variant="body2" className={classes.noteUniversity}>
                                                        {attendedMeta.universityName}
                                                    </Typography>
                                                    {attendedMeta.staffText ? (
                                                        <Typography variant="body2" className={classes.noteStaff}>
                                                            ({attendedMeta.staffText})
                                                        </Typography>
                                                    ) : null}
                                                    <Typography variant="body2" className={classes.warningNotesState}>
                                                        : Student attended but notes not added
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Grid>
                            ) : null}
                        </Grid>
                    </Box>
                );
        });
    };

    return (
        <>
            <Box className={classes.actionsRow}>
                <Button
                    variant="outlined"
                    color="primary"
                    startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <GetAppIcon />}
                    onClick={handleExport}
                    disabled={loading || exporting}
                >
                    Export Excel
                </Button>
                <Button
                    variant="outlined"
                    color="primary"
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={loading}
                >
                    Refresh
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                    disabled={loading}
                >
                    Apply Filters
                </Button>
            </Box>

            <Box className={classes.filtersCard}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                        <TextField
                            className={classes.filterField}
                            label="Attendance"
                            variant="outlined"
                            size="small"
                            select
                            value={filters.attendance_filter}
                            onChange={handleFilterChange("attendance_filter")}
                        >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="attended">Attended</MenuItem>
                            <MenuItem value="non-attended">Non-attended</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            className={classes.filterField}
                            label="University"
                            variant="outlined"
                            size="small"
                            value={filters.university}
                            onChange={handleFilterChange("university")}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            className={classes.filterField}
                            label="Student Name"
                            variant="outlined"
                            size="small"
                            value={filters.student_name}
                            onChange={handleFilterChange("student_name")}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            className={classes.filterField}
                            label="Unique Code"
                            variant="outlined"
                            size="small"
                            value={filters.unique_code}
                            onChange={handleFilterChange("unique_code")}
                        />
                    </Grid>
                </Grid>
            </Box>

            {renderStudentRows()}

            <Pagination
                page={page}
                rowsPerPage={rowsPerPage}
                total={total}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                disabled={loading}
            />
        </>
    );
}

StudentReportTab.propTypes = {
    fairId: PropTypes.string.isRequired,
    onTotalStudentsChange: PropTypes.func.isRequired,
    jsonUrl: PropTypes.string,
};
